import { type ButtonHTMLAttributes, forwardRef } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/cn'

/**
 * `size='icon'` is the square 20px icon-only button — a chip field's trailing
 * affordance, a toast dismiss, a section-header action. It drops the text padding
 * the `sm`/`md` sizes carry and tightens the radius to `rounded-sm` (4px), which
 * reads correctly at this size where the text button radius does not. The box is deliberately
 * larger than every glyph it holds; that margin IS the button's padding, since the
 * glyph defaults to the shared icon size; explicit size utilities can override it.
 *
 * Glyphs also draw one step thinner than the 1.55 the icon set ships, so a lone
 * icon reads as a secondary affordance rather than a piece of UI text. CSS wins
 * over the SVG's own `stroke-width` attribute, so this reaches every stroked icon
 * without touching the icon components — including the few that ship at 2.
 *
 * Compose it with `quiet` (the usual choice) or `ghost` (where the surrounding
 * surface owns the hover) — those pairings also pick up the muted icon color.
 *
 * @example <Button variant='quiet' size='icon' aria-label='Dismiss'><X className='size-[16px]' /></Button>
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-[var(--s-3)] [&>svg]:shrink-0 [&>svg:not([class*='size-']):not([class*='w-']):not([class*='h-'])]:size-[var(--s-7)] transition-colors disabled:pointer-events-none disabled:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] rounded-[var(--radius-button)]",
  {
    variants: {
      variant: {
        default:
          'text-[var(--text-body)] bg-[var(--surface-1)] border border-[var(--border)] hover-hover:bg-[var(--surface-4)] hover-hover:border-[var(--border-strong)] active:bg-[var(--surface-5)]',
        active:
          'bg-[var(--surface-5)] hover-hover:bg-[var(--surface-6)] text-[var(--text-primary)] hover-hover:text-[var(--text-primary)] border border-[var(--border)] hover-hover:border-[var(--border)] dark:hover-hover:bg-[var(--border)]',
        '3d': 'text-[var(--text-tertiary)] border-t border-l border-r border-[var(--border)] shadow-[0_2px_0_0_var(--border)] hover-hover:shadow-[0_4px_0_0_var(--border)] transition-[transform,box-shadow,color] hover-hover:-translate-y-0.5 hover-hover:text-[var(--text-primary)]',
        outline:
          'text-[var(--text-body)] border border-[var(--border)] bg-transparent hover-hover:bg-[var(--surface-4)] hover-hover:border-[var(--border-strong)] active:bg-[var(--surface-5)]',
        primary:
          'bg-[var(--text-primary)] text-[var(--text-inverse)] hover-hover:text-[var(--text-inverse)] hover-hover:bg-[var(--text-body)] dark:bg-white dark:text-[var(--bg)] dark:hover-hover:bg-[var(--text-secondary)] dark:hover-hover:text-[var(--bg)]',
        destructive:
          'bg-[var(--text-error)] text-white hover-hover:text-white hover-hover:brightness-106',
        secondary: 'bg-[var(--brand-secondary)] text-[var(--text-primary)]',
        tertiary:
          'bg-[var(--brand-accent)] text-[var(--text-inverse)] hover-hover:text-[var(--text-inverse)] hover-hover:bg-[var(--brand-accent-hover)] dark:bg-[var(--brand-accent)] dark:hover-hover:bg-[var(--brand-accent-hover)] dark:text-[var(--text-inverse)] dark:hover-hover:text-[var(--text-inverse)]',
        ghost: 'text-[var(--text-secondary)] hover-hover:text-[var(--text-primary)]',
        subtle:
          'text-[var(--text-body)] hover-hover:text-[var(--text-body)] hover-hover:bg-[var(--surface-4)]',
        'ghost-secondary': 'text-[var(--text-muted)] hover-hover:text-[var(--text-primary)]',
        quiet: 'text-[var(--text-secondary)] hover-hover:bg-[var(--surface-active)]',
        /**
         * Text that acts, inline in a sentence or at the end of a row. It carries
         * no surface of its own, so it keeps the caller's type size rather than
         * the button scale — a button that looks like a word has to sit on the
         * line the word is on.
         */
        link: 'text-[var(--text-primary)] underline underline-offset-4 hover-hover:text-[var(--text-body)]',
      },
      size: {
        sm: 'px-1.5 py-1 text-[length:11px]',
        md: 'h-[var(--control-h)] px-[var(--s-6)] py-0 text-[length:var(--t-sm)] font-[var(--w-medium)] leading-[var(--lh-body)]',
        icon: 'size-[20px] rounded-sm p-0 [&_svg]:[stroke-width:1.25]',
      },
    },
    compoundVariants: [
      /* See the `link` variant: no surface means no padding and no size step. */
      { variant: 'link', size: 'sm', className: 'p-0 text-[length:inherit]' },
      { variant: 'link', size: 'md', className: 'h-auto p-0 text-[length:inherit] font-[inherit]' },
      /**
       * A lone glyph is icon content, not text, so the neutral icon buttons paint
       * with `--text-icon-muted` rather than the variant's text color. Scoped to
       * the neutral variants: the filled ones (`primary`, `destructive`, …) carry
       * inverse text that must keep winning over their own surface.
       */
      { size: 'icon', variant: 'quiet', className: 'text-[var(--text-icon-muted)]' },
      { size: 'icon', variant: 'ghost', className: 'text-[var(--text-icon-muted)]' },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Render the child element with this button's chrome instead of a `<button>`.
   *
   * For the case where the thing is a link and looks like a button: a `<button>`
   * wrapping an `<a>` is not navigable by the keyboard's link affordances, and
   * an `<a>` wrapping a `<button>` is a control inside a control.
   */
  asChild?: boolean
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        data-ui-button=''
        data-button-variant={variant ?? 'default'}
        data-button-size={size ?? 'md'}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    )
  }
)

Button.displayName = 'Button'

export { Button, buttonVariants }
